import { Badge } from '~shared/shadcn/ui/badge';

import {
  normalizeInvoiceStatus,
  resolvePostedOrChargedLabel,
} from './hourly-tuition-posted-charged';

export {
  normalizeInvoiceStatus,
  resolvePostedOrChargedLabel,
} from './hourly-tuition-posted-charged';

export function resolveStudentInvoiceStatus(
  studentData: {
    postedInvoiceStatus?: string | null;
    enrollments?: Array<{
      weeklyBreakdown?: Array<{ postedInvoiceStatus?: string | null }>;
    }>;
  },
  processedInfo?: { invoiceStatus?: string | null } | null,
): string | null {
  if (studentData.postedInvoiceStatus?.trim()) {
    return studentData.postedInvoiceStatus;
  }

  if (processedInfo?.invoiceStatus?.trim()) {
    return processedInfo.invoiceStatus;
  }

  for (const enrollment of studentData.enrollments ?? []) {
    for (const week of enrollment.weeklyBreakdown ?? []) {
      if (week.postedInvoiceStatus?.trim()) {
        return week.postedInvoiceStatus;
      }
    }
  }

  return null;
}

export function isStudentFullyCharged(group: {
  isAlreadyProcessed?: boolean;
}): boolean {
  return !!group.isAlreadyProcessed;
}

export function formatHourlyTuitionAmount(
  amount: number | null | undefined,
): string {
  if (amount == null || Number.isNaN(Number(amount))) {
    return '0.00';
  }
  return (Math.round(Number(amount) * 100) / 100).toFixed(2);
}

export {
  isHourlySessionOutOfRange,
  isHourlyWeekOutOfRange,
  resolveHourlySessionApplicableRange,
} from './hourly-tuition-range';
export type { HourlyApplicableRange } from './hourly-tuition-range';

export function getChargeStatusBadge(isCharged: boolean) {
  if (isCharged) {
    return (
      <Badge
        variant='outline'
        className='border-orange-200 bg-orange-50 text-xs font-medium text-orange-700'
      >
        Charged
      </Badge>
    );
  }

  return (
    <Badge
      variant='outline'
      className='border-emerald-200 bg-emerald-50 text-xs font-medium text-emerald-700'
    >
      Not Charged
    </Badge>
  );
}

function getPostedOrChargedBadge(
  label: ReturnType<typeof resolvePostedOrChargedLabel>,
) {
  switch (label) {
    case 'Charged':
      return getChargeStatusBadge(true);
    case 'Not Charged':
      return getChargeStatusBadge(false);
    case 'Posted':
      return (
        <Badge
          variant='outline'
          className='border-sky-200 bg-sky-50 text-xs font-medium text-sky-800'
        >
          Posted
        </Badge>
      );
    case 'Partially Charged':
      return (
        <Badge
          variant='outline'
          className='border-amber-200 bg-amber-50 text-xs font-medium text-amber-800'
        >
          Partially Charged
        </Badge>
      );
    case 'Partially Posted':
      return (
        <Badge
          variant='outline'
          className='border-amber-200 bg-amber-50 text-xs font-medium text-amber-800'
        >
          Partially Posted
        </Badge>
      );
  }
}

export function getStudentChargeStatusBadge(group: {
  isAlreadyProcessed?: boolean;
  isPartiallyProcessed?: boolean;
  invoiceStatus?: string | null;
  lastPostFailure?: string | null;
}) {
  if (group.lastPostFailure) {
    return (
      <Badge
        variant='outline'
        className='max-w-[220px] truncate border-red-200 bg-red-50 text-xs font-medium text-red-700'
        title={group.lastPostFailure}
      >
        Prior issue: {group.lastPostFailure}
      </Badge>
    );
  }

  return getPostedOrChargedBadge(resolvePostedOrChargedLabel(group));
}

export function getInvoiceStatusBadge(
  status: string | null | undefined,
  options?: { showPrefix?: boolean },
) {
  const normalized = normalizeInvoiceStatus(status);
  const prefix = options?.showPrefix ? 'Invoice Status: ' : '';

  if (!normalized) {
    return (
      <Badge variant='secondary' className='text-xs'>
        {prefix}No Invoice
      </Badge>
    );
  }

  switch (normalized) {
    case 'paid':
      return (
        <Badge
          variant='default'
          className='bg-green-100 text-xs text-green-800'
        >
          {prefix}Paid
        </Badge>
      );
    case 'partially paid':
      return (
        <Badge
          variant='secondary'
          className='bg-amber-100 text-xs text-amber-800'
        >
          {prefix}Partially Paid
        </Badge>
      );
    case 'pending':
    case 'processing':
      return (
        <Badge
          variant='secondary'
          className='bg-yellow-100 text-xs text-yellow-800'
        >
          {prefix}Pending
        </Badge>
      );
    case 'failed':
      return (
        <Badge
          variant='destructive'
          className='bg-red-100 text-xs text-red-800'
        >
          {prefix}Failed
        </Badge>
      );
    case 'cancelled':
    case 'void':
      return (
        <Badge variant='outline' className='bg-gray-100 text-xs text-gray-800'>
          {prefix}
          {normalized === 'void' ? 'Void' : 'Cancelled'}
        </Badge>
      );
    case 'scheduled':
    case 'invoice scheduled':
      return (
        <Badge
          variant='secondary'
          className='bg-blue-100 text-xs text-blue-800'
        >
          {prefix}Scheduled
        </Badge>
      );
    case 'full refund':
    case 'partial refund':
      return (
        <Badge variant='outline' className='bg-gray-100 text-xs text-gray-800'>
          {prefix}
          {normalized === 'full refund' ? 'Full Refund' : 'Partial Refund'}
        </Badge>
      );
    default:
      return (
        <Badge variant='outline' className='text-xs capitalize'>
          {prefix}
          {normalized}
        </Badge>
      );
  }
}

export function shouldShowPostedWeekChargeStatus(week: {
  isPosted?: boolean | null;
  minutes?: number;
  billingAmount?: number;
  applicableRange?: {
    minMinute: number;
    maxMinute: number;
    flatRate: number;
  } | null;
  lastMonthTuitionPrepaid?: boolean;
}): boolean {
  if (!week.isPosted) {
    return false;
  }
  if (week.lastMonthTuitionPrepaid) {
    return false;
  }
  if ((week.minutes ?? 0) <= 0 && (week.billingAmount ?? 0) <= 0) {
    return false;
  }
  return !!week.applicableRange;
}

export function getWeekChargeStatusBadge(week: {
  isPosted?: boolean | null;
  postedInvoiceStatus?: string | null;
  minutes?: number;
  billingAmount?: number;
  applicableRange?: {
    minMinute: number;
    maxMinute: number;
    flatRate: number;
  } | null;
  lastMonthTuitionPrepaid?: boolean;
}) {
  if (!shouldShowPostedWeekChargeStatus(week)) return null;

  return getPostedOrChargedBadge(
    resolvePostedOrChargedLabel({
      isAlreadyProcessed: true,
      invoiceStatus: week.postedInvoiceStatus,
    }),
  );
}

export function getWeekInvoiceStatusBadge(week: {
  isPosted?: boolean | null;
  postedInvoiceStatus?: string | null;
  minutes?: number;
  billingAmount?: number;
  applicableRange?: {
    minMinute: number;
    maxMinute: number;
    flatRate: number;
  } | null;
  lastMonthTuitionPrepaid?: boolean;
}) {
  if (!shouldShowPostedWeekChargeStatus(week)) return null;
  if (!normalizeInvoiceStatus(week.postedInvoiceStatus)) return null;

  return getInvoiceStatusBadge(week.postedInvoiceStatus);
}

export function isWeekChargeStatusVisible(week: {
  isPosted?: boolean | null;
  minutes?: number;
  billingAmount?: number;
  applicableRange?: {
    minMinute: number;
    maxMinute: number;
    flatRate: number;
  } | null;
  lastMonthTuitionPrepaid?: boolean;
}): boolean {
  return shouldShowPostedWeekChargeStatus(week);
}
